import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar as CalendarIcon, Users, Tag, ArrowRight, Star, MapPin, Award } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { trackEvent } from '@/lib/analytics';
import { suitesData } from '@/data/suitesData';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { pt, enUS, es } from 'date-fns/locale';

const Home = () => {
  const { t, language } = useLanguage();
  const [dateRange, setDateRange] = useState({
    from: undefined,
    to: undefined
  });
  const [bookingData, setBookingData] = useState({
    guests: `2 ${t('home.people')}`,
    promo: ''
  });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detectar tamanho da tela para ajustar número de meses no calendário
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getLocale = () => {
    switch(language) {
      case 'pt': return pt;
      case 'es': return es;
      default: return enUS;
    }
  };

  const formatDateRange = () => {
    if (!dateRange.from) return '';
    if (!dateRange.to) return format(dateRange.from, 'dd/MM/yyyy', { locale: getLocale() });
    return `${format(dateRange.from, 'dd/MM', { locale: getLocale() })} - ${format(dateRange.to, 'dd/MM/yyyy', { locale: getLocale() })}`;
  };

  const guestOptions = [
    `1 ${t('home.person')}`,
    `2 ${t('home.people')}`,
    `3 ${t('home.people')}`,
    `4${t('home.morePeople')}`
  ];

  const sendWhatsApp = () => {
    const phone = '5531991640648';
    const dates = formatDateRange() || t('whatsapp.toArrange');
    const guests = bookingData.guests;
    const promo = bookingData.promo;

    let message = `${t('whatsapp.hello')}%0A%0A`;
    message += `${t('whatsapp.period')} ${dates}%0A`;
    message += `${t('whatsapp.guests')} ${guests}%0A`;
    if (promo) message += `${t('whatsapp.code')} ${promo.toUpperCase()}%0A`;
    message += `%0A${t('whatsapp.checkAvailability')}`;

    trackEvent('whatsapp_click', { origem: 'home_disponibilidade', page_path: window.location.pathname });
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  const getSuiteTranslation = (suiteId) => {
    return {
      name: t(`suites.${suiteId}.name`),
      subtitle: t(`suites.${suiteId}.subtitle`)
    };
  };

  return (
    <div className="min-h-screen bg-[#f6f6f8]">
      {/* Hero Section */}
      <div
        className="relative w-full h-screen min-h-[600px] flex flex-col items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url("https://lh3.googleusercontent.com/u/0/d/11C--r8vi0ucAg5TfFFO6O6ihWVMwDZ-U")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-20 text-center max-w-4xl px-4 flex flex-col items-center gap-6 mt-10 animate-fade-in">
          <h1 className="text-white text-5xl md:text-7xl font-extrabold leading-tight tracking-tight" data-testid="hero-title">
            {t('home.heroTitle')}<br />{t('home.heroSubtitle').split(' ')[0]} <span className="text-white italic font-serif opacity-90">{t('home.heroSubtitle').split(' ').slice(1).join(' ')}</span>
          </h1>

          {/* Booking Widget */}
          <div className="w-full mt-10 max-w-5xl animate-slide-up" data-testid="booking-widget">
            <div className="bg-white p-4 rounded-xl shadow-2xl flex flex-col md:flex-row gap-3 border border-gray-100">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Dates */}
                <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                  <PopoverTrigger asChild>
                    <div className="group hover:bg-gray-50 rounded-lg p-3 border border-transparent hover:border-gray-200 transition-all text-left cursor-pointer">
                      <div className="flex items-center gap-3">
                        <CalendarIcon className="text-[#C5A059] w-5 h-5" />
                        <div className="flex flex-col w-full">
                          <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                            {t('home.checkInOut')}
                          </span>
                          <span 
                            className={`text-sm font-bold ${formatDateRange() ? 'text-slate-900' : 'text-slate-400'}`}
                            data-testid="booking-dates-input"
                          >
                            {formatDateRange() || t('home.checkInPlaceholder')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white" align="start">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={(range) => {
                        setDateRange(range || { from: undefined, to: undefined });
                        if (range?.to) {
                          setIsCalendarOpen(false);
                        }
                      }}
                      numberOfMonths={isMobile ? 1 : 2}
                      locale={getLocale()}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      className="rounded-md border-0"
                      classNames={{
                        day_selected: "bg-[#C5A059] text-white hover:bg-[#C5A059] hover:text-white focus:bg-[#C5A059] focus:text-white",
                        day_range_middle: "aria-selected:bg-[#C5A059]/20 aria-selected:text-slate-900",
                        day_today: "bg-gray-100 text-slate-900",
                      }}
                    />
                  </PopoverContent>
                </Popover>

                {/* Guests */}
                <div className="group hover:bg-gray-50 rounded-lg p-3 border border-transparent hover:border-gray-200 transition-all text-left">
                  <div className="flex items-center gap-3">
                    <Users className="text-[#C5A059] w-5 h-5" />
                    <div className="flex flex-col w-full">
                      <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                        {t('home.guests')}
                      </span>
                      <select
                        value={bookingData.guests}
                        onChange={(e) => setBookingData({ ...bookingData, guests: e.target.value })}
                        className="bg-transparent border-none p-0 text-sm font-bold text-slate-900 focus:ring-0 w-full cursor-pointer"
                        data-testid="booking-guests-select"
                      >
                        {guestOptions.map((option, idx) => (
                          <option key={idx} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Promo Code */}
                <div className="group hover:bg-gray-50 rounded-lg p-3 border border-transparent hover:border-gray-200 transition-all text-left">
                  <div className="flex items-center gap-3">
                    <Tag className="text-[#C5A059] w-5 h-5" />
                    <div className="flex flex-col w-full">
                      <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                        {t('home.promoCode')}
                      </span>
                      <input
                        type="text"
                        placeholder={t('home.promoPlaceholder')}
                        value={bookingData.promo}
                        onChange={(e) => setBookingData({ ...bookingData, promo: e.target.value })}
                        className="bg-transparent border-none p-0 text-sm font-bold text-slate-900 focus:ring-0 w-full placeholder:text-slate-400"
                        data-testid="booking-promo-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={sendWhatsApp}
                className="btn-gold rounded-lg px-8 py-3 font-bold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 md:w-auto w-full"
                data-testid="booking-search-button"
              >
                <span>{t('home.search')}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-16 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-3 animate-slide-up">
              <div className="w-16 h-16 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto">
                <MapPin className="w-8 h-8 text-[#C5A059]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">{t('home.privilegedLocation')}</h3>
              <p className="text-gray-600 text-sm">
                {t('home.privilegedLocationDesc')}
              </p>
            </div>
            <div className="text-center space-y-3 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <div className="w-16 h-16 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto">
                <Star className="w-8 h-8 text-[#C5A059]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">{t('home.exceptionalService')}</h3>
              <p className="text-gray-600 text-sm">
                {t('home.exceptionalServiceDesc')}
              </p>
            </div>
            <div className="text-center space-y-3 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="w-16 h-16 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto">
                <Award className="w-8 h-8 text-[#C5A059]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">{t('home.luxuryHistory')}</h3>
              <p className="text-gray-600 text-sm">
                {t('home.luxuryHistoryDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Suites Section */}
      <section className="py-20 bg-[#f6f6f8]" data-testid="suites-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-slide-up">
            <h2 className="text-4xl font-extrabold mb-4 text-slate-900">{t('home.ourAccommodations')}</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              {t('home.ourAccommodationsDesc')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {suitesData.map((suite, index) => {
              const translation = getSuiteTranslation(suite.id);
              return (
                <Link
                  key={suite.id}
                  to={`/suite/${suite.id}`}
                  className="group relative overflow-hidden rounded-xl h-[400px] hover-scale image-hover-zoom"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`suite-card-${suite.id}`}
                >
                  <img
                    src={suite.mainImage}
                    alt={translation.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  <div className="absolute bottom-0 p-6 text-white space-y-2">
                    <h3 className="text-2xl font-bold">{translation.name}</h3>
                    <p className="text-sm text-gray-300">{translation.subtitle}</p>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/suites"
              className="inline-flex items-center gap-2 btn-gold rounded-lg px-8 py-3 font-bold text-base"
              data-testid="view-all-suites-button"
            >
              {t('home.viewAllSuites')}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="relative py-32 parallax"
        style={{
          backgroundImage: 'url("https://lh3.googleusercontent.com/u/0/d/1w6WFPY4NmIxWeOxDXwMuLqA4I0q6UjXe")'
        }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-[#101B2D]/70"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center text-white space-y-6">
          <h2 className="text-4xl md:text-5xl font-extrabold">{t('home.readyToLiveHistory')}</h2>
          <p className="text-xl text-gray-200">
            {t('home.readyToLiveHistoryDesc')}
          </p>
          <a
            href={`https://wa.me/5531991640648?text=${encodeURIComponent(t('whatsapp.hello').replace('*Pousada do Mondego*', 'Pousada do Mondego'))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 btn-gold rounded-lg px-10 py-4 font-bold text-lg"
            data-testid="cta-reserve-button"
          >
            {t('nav.reserveNow')}
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
